import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import clientService from '../../services/clientService';
import './ClientCompact.css';

const clientName = (client) => client.typeClient === 'ENTREPRISE'
    ? client.entreprise || client.nom || ''
    : [client.nom, client.prenom].filter(Boolean).join(' ');

const ClientForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const clientId = id || location.state?.client?.id;
    const [original, setOriginal] = useState(null);
    const [name, setName] = useState('');
    const [identityKind, setIdentityKind] = useState('CIN');
    const [identities, setIdentities] = useState({ CIN: '', ICE: '' });
    const identity = identities[identityKind];
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(Boolean(clientId));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const submitting = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setError('');
        setLoadFailed(false);
        if (!clientId) {
            setOriginal(null);
            setName('');
            setIdentityKind('CIN');
            setIdentities({ CIN: '', ICE: '' });
            setPhone('');
            setLoading(false);
            return;
        }
        setLoading(true);
        // Fetch the complete record so hidden fields and projects survive an edit.
        clientService.getClientById(clientId).then(({ data }) => {
            if (cancelled) return;
            const client = data.body;
            if (!client) throw new Error('Client introuvable');
            setOriginal(client);
            setName(clientName(client));
            const kind = client.typeClient === 'ENTREPRISE' ? 'ICE' : 'CIN';
            setIdentityKind(kind);
            setIdentities({ CIN: client.cin || '', ICE: client.ice || '' });
            setPhone(client.telephone1 || '');
        }).catch(() => {
            if (cancelled) return;
            setLoadFailed(true);
            setError('Impossible de charger le client. Réessayez.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [clientId, loadAttempt]);

    const goBack = () => {
        if (location.pathname.startsWith('/sales-module')) navigate('/sales-module/clients');
        else navigate(-1);
    };

    const changeIdentityKind = (kind) => {
        setIdentityKind(kind);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting.current || loading || loadFailed) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Saisissez le nom du client.');
            return;
        }
        const company = identityKind === 'ICE';
        const samePersonalName = original?.typeClient === 'PARTICULIER'
            && !company && trimmedName === clientName(original);
        const payload = {
            ...(original || { active: true, plafond: 0, chiffreAffaires: 0, solde: 0 }),
            typeClient: company ? 'ENTREPRISE' : 'PARTICULIER',
            nom: company ? original?.nom || '' : samePersonalName ? original.nom : trimmedName,
            prenom: company ? original?.prenom || '' : samePersonalName ? original.prenom : '',
            entreprise: company ? trimmedName : original?.entreprise || '',
            ice: identities.ICE.trim(),
            cin: identities.CIN.trim(),
            telephone1: phone.trim(),
            chantiers: original?.chantiers || [],
        };
        submitting.current = true;
        setSaving(true);
        setError('');
        try {
            if (clientId) await clientService.update(clientId, payload);
            else await clientService.addClient(payload);
            goBack();
        } catch {
            setError('Enregistrement impossible. Vérifiez les informations et réessayez.');
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return (
        <section className="client-compact client-compact-form">
            <header className="client-compact-header">
                <h4>{clientId ? 'Modifier le client' : 'Nouveau client'}</h4>
                <Button color="secondary" outline size="sm" onClick={goBack} disabled={saving}>Retour</Button>
            </header>
            <div className="client-compact-panel">
                {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
                {loading ? <div className="client-compact-loading"><Spinner size="sm" /> Chargement…</div>
                    : loadFailed ? <Button size="sm" color="primary" onClick={() => setLoadAttempt(value => value + 1)}>Réessayer</Button>
                    : <form onSubmit={handleSubmit}>
                        <fieldset disabled={saving}>
                            <div className="client-compact-fields">
                                <div>
                                    <label htmlFor="client-name">Nom <span aria-hidden="true">*</span></label>
                                    <input id="client-name" className="form-control" value={name} onChange={event => setName(event.target.value)} required autoFocus autoComplete="name" placeholder="Nom du client ou de l’entreprise" />
                                </div>
                                <div>
                                    <label htmlFor="client-identity">ICE / CIN</label>
                                    <div className="input-group">
                                        <select className="form-select client-identity-kind" aria-label="Type de numéro : CIN ou ICE" value={identityKind} onChange={event => changeIdentityKind(event.target.value)}>
                                            <option value="CIN">CIN</option>
                                            <option value="ICE">ICE</option>
                                        </select>
                                        <input id="client-identity" className="form-control" value={identity} onChange={event => setIdentities(values => ({ ...values, [identityKind]: event.target.value }))} maxLength={identityKind === 'ICE' ? 15 : 20} placeholder={identityKind === 'ICE' ? 'Numéro ICE' : 'Numéro CIN'} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="client-phone">Téléphone</label>
                                    <input id="client-phone" className="form-control" type="tel" autoComplete="tel" value={phone} onChange={event => setPhone(event.target.value)} placeholder="06…" />
                                </div>
                            </div>
                            <footer className="client-compact-form-footer">
                                <small className="text-muted">Seul le nom est obligatoire.</small>
                                <Button color="primary" size="sm" type="submit">{saving && <Spinner size="sm" className="me-2" />}{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
                            </footer>
                        </fieldset>
                    </form>}
            </div>
        </section>
    );
};

export default ClientForm;
